import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

// Fictional local frame: x east 0..3000 m, y north 0..2000 m. SVG y is flipped.
const W = 3000, H = 2000;
const sx = (x) => x;
const sy = (y) => H - y;
const pts = (poly) => poly.map(([x, y]) => `${sx(x)},${sy(y)}`).join(" ");

const VESSEL_SHAPE = { CONTAINER: 90, BULK_CARRIER: 80, TANKER: 78, GENERAL_CARGO: 65, TUG: 30, RO_RO: 70 };

export function PortMap({ geometry, state, selectedStation, onSelectStation, height = 520, compact = false, testId = "port-map" }) {
  const navigate = useNavigate();
  const wind = state?.wind;
  const corridor = state?.attribution?.corridor;
  const candidateKeys = useMemo(() => new Set((state?.attribution?.candidates || []).map((c) => c.source_key)), [state]);
  if (!geometry) return null;
  const windTo = wind ? (wind.wind_from_deg + 180) % 360 : null;

  return (
    <div data-testid={testId} className="relative overflow-hidden rounded border border-[#1E3E30] bg-[#0C1B15]" style={{ height }}>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-full w-full" preserveAspectRatio="xMidYMid meet">
        <defs>
          <pattern id="water" width="60" height="60" patternUnits="userSpaceOnUse"><path d="M0 30 Q15 20 30 30 T60 30" fill="none" stroke="#0e3a52" strokeWidth="2" /></pattern>
          <marker id="arrow" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><path d="M0,0 L8,4 L0,8 z" fill="#90B849" /></marker>
        </defs>
        <rect width={W} height={H} fill="#0B1712" />
        <rect width={W} height={H} fill="url(#water)" opacity="0.6" />
        <polyline points={pts(geometry.port.channel_centreline)} fill="none" stroke="#1b5a73" strokeWidth="4" strokeDasharray="40 30" />
        <polygon points={pts(geometry.port.shoreline)} fill="#13233b" stroke="#2a4a6b" strokeWidth="4" />
        {geometry.berths.map((b) => (
          <g key={b.berth_id} data-testid={`map-berth-${b.code}`}>
            <polygon points={pts(b.polygon)} fill="#0f3348" stroke="#3b82a0" strokeWidth="3" strokeDasharray="12 8" />
            <text x={sx(b.centre[0])} y={sy(b.polygon[0][1]) - 12} fontSize="34" fill="#7dd3fc" textAnchor="middle" fontFamily="JetBrains Mono">{b.code}</text>
          </g>
        ))}
        {corridor && (
          <g data-testid="map-corridor">
            <polygon points={pts(corridor.envelope)} fill="#F59E0B" fillOpacity="0.14" stroke="#F59E0B" strokeWidth="3" strokeDasharray="14 10" />
            {corridor.ensemble_points.map((p, i) => <circle key={i} cx={sx(p[0])} cy={sy(p[1])} r="6" fill="#F59E0B" fillOpacity="0.5" />)}
          </g>
        )}
        {geometry.activity_sources.map((a) => {
          const active = state?.activity_sources?.find((s) => s.source_id === a.source_id)?.active;
          const cand = candidateKeys.has(a.code);
          return (
            <g key={a.source_id} data-testid={`map-source-${a.code}`}>
              <rect x={sx(a.x) - 22} y={sy(a.y) - 22} width="44" height="44" fill={active ? "#F59E0B" : "#334155"} fillOpacity={active ? 0.9 : 0.6} stroke={cand ? "#fde68a" : "#64748b"} strokeWidth={cand ? 6 : 2} transform={`rotate(45 ${sx(a.x)} ${sy(a.y)})`} />
              {!compact && <text x={sx(a.x)} y={sy(a.y) - 34} fontSize="26" fill="#cbd5e1" textAnchor="middle" stroke="#0C1B15" strokeWidth="6" paintOrder="stroke">{a.name}{cand ? " · candidate" : ""}</text>}
            </g>
          );
        })}
        {geometry.stations.map((s, i) => {
          const st = state?.stations?.find((x) => x.station_id === s.station_id);
          const no2 = st?.latest?.NO2?.value;
          const sel = selectedStation === s.station_id;
          const isMet = s.role === "MET";
          return (
            <g key={s.station_id} data-testid={`map-station-${s.code}`} className="cursor-pointer" onClick={() => (onSelectStation ? onSelectStation(s.station_id) : navigate(`/sensors/${s.station_id}`))}>
              {isMet ? (
                <polygon points={`${sx(s.x)},${sy(s.y) - 34} ${sx(s.x) + 30},${sy(s.y) + 20} ${sx(s.x) - 30},${sy(s.y) + 20}`} fill="#11241C" stroke="#90B849" strokeWidth="5" />
              ) : (
                <circle cx={sx(s.x)} cy={sy(s.y)} r={sel ? 40 : 32} fill="#11241C" stroke={sel ? "#fde68a" : "#90B849"} strokeWidth="6" />
              )}
              <text x={sx(s.x)} y={sy(s.y) + 11} fontSize="30" fontWeight="700" fill="#e0f2fe" textAnchor="middle" fontFamily="JetBrains Mono">{isMet ? "M" : i + 1}</text>
              {!compact && <text x={sx(s.x)} y={sy(s.y) - 48} fontSize="28" fill="#94a3b8" textAnchor="middle">{s.name}{no2 != null ? ` · NO₂ ${Math.round(no2)}` : ""}</text>}
            </g>
          );
        })}
        {state?.vessels?.map((v, vi) => {
          if (!v.position) return null;
          const L = VESSEL_SHAPE[v.vessel_type] || 60;
          const interp = v.ais_kind === "INTERPOLATED";
          const cand = candidateKeys.has(v.vessel_id);
          const rank = state?.attribution?.candidates?.find((c) => c.source_key === v.vessel_id)?.rank;
          const labelY = sy(v.position[1]) + 48 + (vi % 2) * 30;
          return (
            <g key={v.vessel_id} data-testid={`map-vessel-${v.vessel_id}`} className="cursor-pointer" onClick={() => navigate(`/vessels/${v.vessel_id}`)}>
              <polygon points={`${sx(v.position[0]) - L},${sy(v.position[1]) - 16} ${sx(v.position[0]) + L - 24},${sy(v.position[1]) - 16} ${sx(v.position[0]) + L},${sy(v.position[1])} ${sx(v.position[0]) + L - 24},${sy(v.position[1]) + 16} ${sx(v.position[0]) - L},${sy(v.position[1]) + 16}`}
                fill={cand ? "#F59E0B" : "#e2e8f0"} fillOpacity={interp ? 0.45 : 0.95} stroke={cand ? "#fde68a" : "#94a3b8"} strokeWidth="3" strokeDasharray={interp ? "10 8" : "0"} />
              {!compact && <text x={sx(v.position[0])} y={labelY} fontSize="26" fill={cand ? "#fde68a" : "#cbd5e1"} textAnchor="middle" stroke="#0C1B15" strokeWidth="6" paintOrder="stroke">{v.name}{interp ? " (interp.)" : ""}{cand ? ` · candidate #${rank}` : ""}</text>}
            </g>
          );
        })}
        {wind && windTo != null && (
          <g data-testid="map-wind-arrow" transform={`translate(${W - 260} 240)`}>
            <circle r="150" fill="#11241C" fillOpacity="0.85" stroke="#1E3E30" strokeWidth="3" />
            <line x1={-110 * Math.sin((windTo * Math.PI) / 180)} y1={110 * Math.cos((windTo * Math.PI) / 180)} x2={110 * Math.sin((windTo * Math.PI) / 180)} y2={-110 * Math.cos((windTo * Math.PI) / 180)} stroke="#90B849" strokeWidth="8" markerEnd="url(#arrow)" />
            <text y="-120" fontSize="26" fill="#cbd5e1" textAnchor="middle" fontFamily="JetBrains Mono">WIND → TO {Math.round(windTo)}°</text>
            <text y="134" fontSize="24" fill="#e0f2fe" textAnchor="middle" fontFamily="JetBrains Mono">FROM {Math.round(wind.wind_from_deg)}° · {wind.wind_speed_mps.toFixed(1)} m/s</text>
          </g>
        )}
        <text x="30" y={H - 30} fontSize="26" fill="#475569" fontFamily="JetBrains Mono">DEMO_SYNTHETIC · fictional geometry · 1 unit = 1 m (local frame)</text>
      </svg>
      {state?.ais_status === "UNAVAILABLE" && (
        <div data-testid="map-ais-unavailable" className="absolute left-4 top-4 rounded border border-slate-500/50 bg-slate-900/90 px-3 py-2 font-mono text-xs text-slate-200 backdrop-blur">
          AIS UNAVAILABLE for this window — not an empty harbour
        </div>
      )}
      <div className={cn("absolute bottom-3 right-3 flex flex-wrap gap-3 rounded border border-[#1E3E30] bg-[#11241C]/90 px-3 py-2 text-[10px] text-slate-400 backdrop-blur")}>
        <span><span className="mr-1 inline-block h-2 w-2 rounded-full border-2 border-lime-400" />station</span>
        <span><span className="mr-1 inline-block h-2 w-3 bg-slate-200" />vessel (observed)</span>
        <span><span className="mr-1 inline-block h-2 w-3 border border-dashed border-slate-300 bg-slate-200/40" />vessel (interpolated)</span>
        <span><span className="mr-1 inline-block h-2 w-2 rotate-45 bg-amber-500" />activity source</span>
        <span><span className="mr-1 inline-block h-2 w-3 border border-dashed border-amber-400 bg-amber-500/20" />source corridor (candidate zone)</span>
      </div>
    </div>
  );
}
